import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import { useWearables, useAppleHealthImport } from '../../hooks/useWearables';
import {
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Badge,
  Skeleton,
  Alert,
  Tabs,
  TextInput,
  Select,
  Modal,
} from '@mantine/core';
import {
  IconLink,
  IconRefresh,
  IconCloudDownload,
  IconDeviceWatch,
  IconUser,
  IconApple,
} from '@tabler/icons-react';

const SYNC_RANGE_PRESETS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last 1 year' },
  { value: '1825', label: 'Last 5 years' },
  { value: 'custom', label: 'Custom range' },
];

function daysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

const providerIcons = {
  garmin: 'https://upload.wikimedia.org/wikipedia/commons/9/9b/Garmin_logo.svg',
  polar: 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Polar_Logo.svg',
  suunto: 'https://upload.wikimedia.org/wikipedia/commons/c/c0/Suunto_logo.svg',
  whoop: null,
  strava: 'https://upload.wikimedia.org/wikipedia/commons/2/26/Strava_Logo.svg',
};

function Devices() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const fromOnboarding = searchParams.get('from') === 'onboarding';
  const {
    providers,
    connections,
    loading,
    error,
    connectProvider,
    syncData,
    refresh,
    refreshProviderUsingLastRange,
  } = useWearables();
  const { importAppleHealth, loading: importLoading, error: importError, result: importResult } = useAppleHealthImport();

  const [connectingProvider, setConnectingProvider] = useState(null);
  const [syncingProvider, setSyncingProvider] = useState(null);
  const [status, setStatus] = useState({ kind: 'idle', message: '' });
  const [appleFileKey, setAppleFileKey] = useState('');
  const [syncModal, setSyncModal] = useState({ open: false, provider: null });
  const [syncRangePreset, setSyncRangePreset] = useState('30');
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate, setSyncEndDate] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success') === 'true' || params.get('oauth_callback') === 'true';
    if (success) {
      window.history.replaceState({}, '', window.location.pathname);
      refresh();
    }
  }, [refresh]);

  const connectedProviders = new Set(connections.map((c) => (c.provider || '').toLowerCase()));

  const handleConnect = async (providerName) => {
    setConnectingProvider(providerName);
    setStatus({ kind: 'loading', message: `Redirecting to ${providerName}...` });
    try {
      const redirectUri = `${window.location.origin}/wearables/devices?oauth_callback=true`;
      await connectProvider(providerName, redirectUri);
    } catch (e) {
      setConnectingProvider(null);
      setStatus({ kind: 'idle', message: '' });
    }
  };

  const openSyncModal = (providerName) => {
    setSyncRangePreset('30');
    setSyncStartDate('');
    setSyncEndDate('');
    setSyncModal({ open: true, provider: providerName });
  };

  const handleSyncConfirm = async () => {
    const providerName = syncModal.provider;
    if (!providerName) return;
    setSyncModal({ open: false, provider: null });
    setSyncingProvider(providerName);

    let startDate = null;
    let endDate = todayISO();
    if (syncRangePreset === 'custom') {
      startDate = syncStartDate || null;
      endDate = syncEndDate || todayISO();
    } else {
      startDate = daysAgoISO(parseInt(syncRangePreset, 10));
    }

    const rangeLabel = syncRangePreset === 'custom'
      ? `${startDate || '?'} to ${endDate}`
      : SYNC_RANGE_PRESETS.find((p) => p.value === syncRangePreset)?.label || syncRangePreset;

    setStatus({ kind: 'syncing', message: `Syncing ${providerName} (${rangeLabel})...` });
    try {
      const result = await syncData(providerName, 'all', { startDate, endDate });
      setStatus({
        kind: 'ready',
        message: result?.is_async
          ? `Sync queued for ${providerName} (${rangeLabel}). Data will appear as it processes.`
          : result?.message || `Sync completed for ${providerName} (${rangeLabel}).`,
      });
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Sync failed';
      setStatus({ kind: 'idle', message: detail });
    } finally {
      setSyncingProvider(null);
    }
  };

  const handleRefreshSync = async (providerName) => {
    setSyncingProvider(providerName);
    setStatus({ kind: 'syncing', message: `Refreshing ${providerName} using your last synced range...` });
    try {
      const result = await refreshProviderUsingLastRange(providerName);
      if (!result?.usedRange?.startDate) {
        setStatus({
          kind: 'idle',
          message: `No previous sync range found for ${providerName}. Please choose a range to sync.`,
        });
        openSyncModal(providerName);
        return;
      }
      const rangeLabel = `${result.usedRange.startDate} to ${result.usedRange.endDate || todayISO()}`;
      setStatus({
        kind: 'ready',
        message: result?.is_async
          ? `Refresh queued for ${providerName} (${rangeLabel}).`
          : result?.message || `Refresh completed for ${providerName} (${rangeLabel}).`,
      });
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Refresh sync failed';
      setStatus({ kind: 'idle', message: detail });
    } finally {
      setSyncingProvider(null);
    }
  };

  const handleAppleImport = async () => {
    if (!appleFileKey.trim()) return;
    try {
      await importAppleHealth(appleFileKey.trim());
      setStatus({ kind: 'ready', message: 'Apple Health import completed.' });
    } catch (e) {
      setStatus({ kind: 'idle', message: '' });
    }
  };

  if (authLoading) {
    return (
      <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
        <Stack gap="md">
          <Skeleton height={40} width={200} />
          <Skeleton height={120} />
        </Stack>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
        <Card withBorder>
          <Title order={2}>Devices</Title>
          <Text c="dimmed" mt="xs">Please log in to access your device settings.</Text>
        </Card>
      </Container>
    );
  }

  return (
    <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
      <Stack gap="md">
        {fromOnboarding && (
          <Alert color="pink" variant="light">
            <Group justify="space-between">
              <Text size="sm">You're setting up your account.</Text>
              <Button component={Link} to="/onboarding" variant="subtle" color="pink" size="compact-sm">
                ← Back to Setup
              </Button>
            </Group>
          </Alert>
        )}
        <div>
          <Title order={1} size="h2" style={{ marginTop: 0 }}>
            Devices
          </Title>
          <Text c="dimmed" mt={6}>
            Manage wearable connections and sync ranges
          </Text>
        </div>

        {status.message && <Alert color="blue" variant="light">{status.message}</Alert>}
        {error && <Alert color="red" variant="light">{error}</Alert>}

        <Tabs defaultValue="devices">
          <Tabs.List>
            <Tabs.Tab value="devices" leftSection={<IconDeviceWatch size={16} />}>
              Connected Devices
            </Tabs.Tab>
            <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
              Profile
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="devices" pt="md">
            <Stack gap="lg">
              <div>
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600} size="lg">Available Providers</Text>
                    <Text size="sm" c="dimmed">Connect wearable providers and sync your selected date ranges</Text>
                  </div>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={refresh}
                    disabled={loading}
                    leftSection={<IconRefresh size={16} />}
                  >
                    Refresh List
                  </Button>
                </Group>

                {loading && providers.length === 0 ? (
                  <Grid>
                    {[1, 2, 3].map((i) => (
                      <Grid.Col key={i} span={{ base: 12, md: 6, lg: 4 }}>
                        <Card withBorder p="md">
                          <Skeleton height={48} width={48} radius="md" />
                          <Skeleton height={20} mt="sm" width="60%" />
                          <Skeleton height={36} mt="md" />
                        </Card>
                      </Grid.Col>
                    ))}
                  </Grid>
                ) : providers.length > 0 ? (
                  <Grid>
                    {providers.map((provider) => {
                      const key = (provider.name || '').toLowerCase();
                      const isConnected = connectedProviders.has(key);
                      const iconUrl = provider.icon_url || providerIcons[key];
                      const displayName = provider.display_name || provider.name || key;

                      return (
                        <Grid.Col key={provider.name} span={{ base: 12, md: 6, lg: 4 }}>
                          <Card withBorder radius="md" p="md">
                            <Group justify="space-between" mb="md">
                              <Group gap="sm">
                                {iconUrl ? (
                                  <img
                                    src={iconUrl}
                                    alt=""
                                    style={{ height: 40, width: 40, objectFit: 'contain' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <IconDeviceWatch size={40} stroke={1.5} color="var(--mantine-color-gray-5)" />
                                )}
                                <div>
                                  <Text fw={600}>{displayName}</Text>
                                  <Badge color={isConnected ? 'green' : 'gray'} variant="light" size="sm" mt={4}>
                                    {isConnected ? 'Connected' : 'Not connected'}
                                  </Badge>
                                </div>
                              </Group>
                            </Group>
                            {isConnected ? (
                              <Group gap="xs">
                                <Button
                                  variant="light"
                                  size="sm"
                                  leftSection={<IconRefresh size={14} />}
                                  onClick={() => handleRefreshSync(provider.name)}
                                  disabled={syncingProvider === provider.name}
                                  loading={syncingProvider === provider.name}
                                >
                                  Refresh
                                </Button>
                                <Button
                                  color="pink"
                                  size="sm"
                                  leftSection={syncingProvider === provider.name ? null : <IconCloudDownload size={14} />}
                                  onClick={() => openSyncModal(provider.name)}
                                  loading={syncingProvider === provider.name}
                                >
                                  Sync Data
                                </Button>
                              </Group>
                            ) : (
                              <Button
                                color="pink"
                                size="sm"
                                leftSection={connectingProvider === provider.name ? null : <IconLink size={14} />}
                                onClick={() => handleConnect(provider.name)}
                                loading={connectingProvider === provider.name}
                                fullWidth
                              >
                                Connect
                              </Button>
                            )}
                          </Card>
                        </Grid.Col>
                      );
                    })}
                  </Grid>
                ) : (
                  <Card withBorder p="md">
                    <Text c="dimmed" ta="center" size="sm">
                      No providers available. Make sure Open Wearables is running.
                    </Text>
                  </Card>
                )}
              </div>

              {connections.length > 0 && (
                <Card withBorder radius="md" p="md">
                  <Title order={4} mb="sm">Your Connections</Title>
                  <Text size="sm" c="dimmed" mb="md">Refresh repeats the last successful sync date range for each provider</Text>
                  <Stack gap="xs">
                    {connections.map((conn) => (
                      <Group key={conn.id} justify="space-between" p="sm" bg="gray.0" style={{ borderRadius: 8 }}>
                        <div>
                          <Text fw={500} tt="capitalize">{conn.provider}</Text>
                          <Text size="xs" c="dimmed">
                            {conn.last_sync
                              ? `Last synced: ${new Date(conn.last_sync).toLocaleString()}`
                              : 'Never synced'}
                          </Text>
                        </div>
                        <Group gap={8}>
                          <Button
                            variant="subtle"
                            size="xs"
                            leftSection={<IconRefresh size={14} />}
                            onClick={() => handleRefreshSync(conn.provider)}
                            disabled={syncingProvider === conn.provider}
                            loading={syncingProvider === conn.provider}
                          >
                            Refresh
                          </Button>
                          <Button
                            variant="subtle"
                            size="xs"
                            color="pink"
                            leftSection={<IconCloudDownload size={14} />}
                            onClick={() => openSyncModal(conn.provider)}
                            disabled={syncingProvider === conn.provider}
                          >
                            Sync Data
                          </Button>
                        </Group>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              )}

              {/* Apple Health import – UI hidden; code kept for future use */}
              {false && (
                <Card withBorder radius="md" p="md">
                  <Group gap="sm" mb="sm">
                    <IconApple size={24} />
                    <div>
                      <Title order={4}>Import Apple Health</Title>
                      <Text size="sm" c="dimmed">Import data from an exported Apple Health XML file (provide file key after upload)</Text>
                    </div>
                  </Group>
                  <Group align="flex-end" gap="sm">
                    <TextInput
                      placeholder="File key"
                      value={appleFileKey}
                      onChange={(e) => setAppleFileKey(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <Button
                      color="pink"
                      onClick={handleAppleImport}
                      loading={importLoading}
                      disabled={!appleFileKey.trim()}
                    >
                      Import
                    </Button>
                  </Group>
                  {importError && (
                    <Alert color="red" variant="light" mt="sm" size="sm">
                      {importError}
                    </Alert>
                  )}
                  {importResult && (
                    <Alert color="green" variant="light" mt="sm" size="sm">
                      Imported: {importResult.records_imported ?? 0} records, {importResult.workouts_imported ?? 0} workouts
                    </Alert>
                  )}
                </Card>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="profile" pt="md">
            <Card withBorder radius="md" p="md">
              <Title order={4} mb="sm">Profile Information</Title>
              <Text size="sm" c="dimmed" mb="md">Your account details</Text>
              <Stack gap="sm">
                <div>
                  <Text size="xs" c="dimmed">Email</Text>
                  <Text fw={500}>{user?.email || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">User ID</Text>
                  <Text size="sm" ff="monospace">{user?.id || 'N/A'}</Text>
                </div>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <Modal
        opened={syncModal.open}
        onClose={() => setSyncModal({ open: false, provider: null })}
        title={`Sync ${syncModal.provider || ''} Data`}
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Choose the exact date range to sync for this provider.
          </Text>

          <Select
            label="Date range"
            data={SYNC_RANGE_PRESETS}
            value={syncRangePreset}
            onChange={(val) => setSyncRangePreset(val || '30')}
          />

          {syncRangePreset === 'custom' && (
            <Group grow>
              <TextInput
                label="Start date"
                type="date"
                value={syncStartDate}
                onChange={(e) => setSyncStartDate(e.target.value)}
                max={todayISO()}
              />
              <TextInput
                label="End date"
                type="date"
                value={syncEndDate}
                onChange={(e) => setSyncEndDate(e.target.value)}
                max={todayISO()}
              />
            </Group>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSyncModal({ open: false, provider: null })}>
              Cancel
            </Button>
            <Button
              color="pink"
              leftSection={<IconCloudDownload size={16} />}
              onClick={handleSyncConfirm}
              disabled={syncRangePreset === 'custom' && !syncStartDate}
            >
              Start Sync
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default Devices;
